import { useRef, useEffect, useCallback, useMemo } from "react";
import { currency } from "../../lib/formatting";
import { propertyMetrics, isSMSF } from "../../lib/calculations";
import { getEntityColor } from "../../lib/entityColors";
import type { PropertyRecord } from "../../types/portfolio";
import type { Structure } from "../../types/entities";

interface SuperFlowProps {
  structures: Structure[];
  properties: PropertyRecord[];
}

export default function SuperFlow({ structures, properties }: SuperFlowProps) {
  const smsfStructures = useMemo(() => structures.filter((s) => isSMSF(s.entityType)), [structures]);

  // Filter to SMSFs that have properties
  const smsfWithProps = useMemo(() => {
    return smsfStructures
      .map((s) => ({
        structure: s,
        props: properties.filter((p) => p.structureId === s.id && p.status !== "sold"),
      }))
      .filter((x) => x.props.length > 0);
  }, [smsfStructures, properties]);

  if (smsfWithProps.length === 0) return null;

  return (
    <div className="space-y-6">
      {smsfWithProps.map((smsf) => (
        <SmsfFlowSection key={smsf.structure.id} structure={smsf.structure} properties={smsf.props} />
      ))}
    </div>
  );
}

function SmsfFlowSection({ structure, properties }: { structure: Structure; properties: PropertyRecord[] }) {
  const gridRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const contributions = structure.monthlyContributions ?? 0;
  const propertyCashflow = properties.reduce((s, p) => s + propertyMetrics(p).netCashflowShare, 0);
  const netFundPosition = propertyCashflow + contributions;

  const drawLines = useCallback(() => {
    const grid = gridRef.current;
    const svg = svgRef.current;
    if (!grid || !svg || window.innerWidth < 768) { if (svg) Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove()); return; }
    const gR = grid.getBoundingClientRect();
    svg.setAttribute("viewBox", `0 0 ${gR.width} ${gR.height}`);
    svg.style.width = `${gR.width}px`;
    svg.style.height = `${gR.height}px`;
    let paths = "";
    const pool = grid.querySelector("[data-pool]");
    if (!pool) { Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove()); return; }
    const pR = pool.getBoundingClientRect();
    const poolLeftX = pR.left - gR.left;
    const poolRightX = pR.right - gR.left;
    const poolTop = pR.top - gR.top;
    const poolH = pR.height;
    const sourceEls = Array.from(grid.querySelectorAll("[data-source]"));
    const destEls = Array.from(grid.querySelectorAll("[data-dest]"));
    const sourceMaxR = sourceEls.length > 0 ? Math.max(...sourceEls.map((el) => el.getBoundingClientRect().right - gR.left)) : poolLeftX;
    const destMinL = destEls.length > 0 ? Math.min(...destEls.map((el) => el.getBoundingClientRect().left - gR.left)) : poolRightX;
    const pullS = Math.max(10, Math.min(40, (poolLeftX - sourceMaxR) * 0.4));
    const pullD = Math.max(10, Math.min(40, (destMinL - poolRightX) * 0.4));

    sourceEls.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const x = r.right - gR.left, y = r.top + r.height / 2 - gR.top;
      const aY = poolTop + (poolH * (i + 1)) / (sourceEls.length + 1);
      paths += `<path marker-end="url(#arrow-super)" d="M ${x} ${y} C ${x + pullS} ${y}, ${poolLeftX - pullS} ${aY}, ${poolLeftX} ${aY}" />`;
    });
    destEls.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const x = r.left - gR.left, y = r.top + r.height / 2 - gR.top;
      const aY = poolTop + (poolH * (i + 1)) / (destEls.length + 1);
      paths += `<path marker-end="url(#arrow-super)" d="M ${poolRightX} ${aY} C ${poolRightX + pullD} ${aY}, ${x - pullD} ${y}, ${x} ${y}" />`;
    });
    Array.from(svg.querySelectorAll("path:not(defs path)")).forEach(p => p.remove());
    if (paths) {
      const tmp = new DOMParser().parseFromString(`<svg xmlns="http://www.w3.org/2000/svg">${paths}</svg>`, "image/svg+xml");
      Array.from(tmp.documentElement.children).forEach(child => svg.appendChild(child));
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(drawLines, 80);
    const h = () => setTimeout(drawLines, 80);
    window.addEventListener("resize", h);
    return () => { clearTimeout(t); window.removeEventListener("resize", h); };
  }, [drawLines, properties.length]);

  const smsfName = structure.displayName || structure.trustName || "SMSF";

  return (
    <div className="space-y-4">
      <div className="relative" ref={gridRef}>
        <svg ref={svgRef} className="absolute inset-0 pointer-events-none hidden md:block" style={{ overflow: "visible" }}>
          <defs>
            <marker id="arrow-super" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse" markerUnits="userSpaceOnUse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#d97706" fillOpacity="0.5" />
            </marker>
          </defs>
          <style>{`svg path { fill: none; stroke: #d97706; stroke-width: 1.5; opacity: 0.5; }`}</style>
        </svg>

        {/* Desktop */}
        <div className="hidden md:grid md:grid-cols-[1fr_180px_1fr] gap-x-12 gap-y-4 items-start">
          <div className="space-y-3">
            <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600">Contributions</div>
            <div data-source className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="text-xs font-medium text-amber-800">{smsfName} contributions</div>
              <div className="text-[10px] text-amber-600">Member + employer SG</div>
              <div className="mt-1 text-sm font-semibold text-amber-900">{currency(Math.round(contributions))}<span className="text-xs font-normal text-amber-600">/mo</span></div>
            </div>
          </div>

          <div className="flex items-center justify-center" style={{ minHeight: 140 }}>
            <div data-pool className="rounded-2xl bg-amber-600 text-white px-5 py-5 text-center w-full">
              <div className="text-[10px] font-medium uppercase tracking-wide text-amber-200">{smsfName}</div>
              <div className="mt-1 text-lg font-bold">{currency(Math.round(contributions))}<span className="text-xs font-normal text-amber-200">/mo</span></div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600">SMSF Properties</div>
            {properties.map((p, i) => {
              const m = propertyMetrics(p);
              const color = getEntityColor(i + 5); // offset from portfolio colors
              const isPositive = m.netCashflowShare >= 0;
              return (
                <div key={p.id} data-dest className="rounded-xl border border-amber-200 bg-white px-4 py-3" style={{ borderRightWidth: 4, borderRightColor: color }}>
                  <div className="text-xs font-medium text-neutral-800 truncate">{p.nickname ?? p.address}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-neutral-400">
                      {p.lender ? `${p.lender}${p.loanAccountNumber ? ` · ****${p.loanAccountNumber.slice(-4)}` : ""}` : "No loan"}
                    </span>
                    <span className={`text-xs font-semibold ${isPositive ? "text-emerald-600" : "text-red-600"}`}>
                      {isPositive ? `+${currency(Math.round(m.netCashflowShare))}` : currency(Math.round(Math.abs(m.netCashflowShare)))}/mo
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-4">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600 mb-2">Contributions</div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between">
              <div><div className="text-xs font-medium text-amber-800">{smsfName}</div><div className="text-[10px] text-amber-600">Member + employer SG</div></div>
              <div className="text-sm font-semibold text-amber-900">{currency(Math.round(contributions))}/mo</div>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="rounded-2xl bg-amber-600 text-white px-6 py-3 text-center">
              <div className="text-[10px] uppercase tracking-wide text-amber-200">{smsfName}</div>
              <div className="text-lg font-bold">{currency(Math.round(contributions))}/mo</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600 mb-2">SMSF Properties</div>
            <div className="space-y-2">
              {properties.map((p) => {
                const m = propertyMetrics(p);
                return (
                  <div key={p.id} className="rounded-xl border border-amber-200 bg-white px-4 py-3">
                    <div className="text-xs font-medium text-neutral-800 truncate">{p.nickname ?? p.address}</div>
                    <div className={`text-xs font-semibold mt-0.5 ${m.netCashflowShare >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {m.netCashflowShare >= 0 ? `+${currency(Math.round(m.netCashflowShare))}` : currency(Math.round(Math.abs(m.netCashflowShare)))}/mo
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-center">
          <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600">Property cashflow</div>
          <div className={`text-sm font-semibold ${propertyCashflow >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {propertyCashflow >= 0 ? `+${currency(Math.round(propertyCashflow))}` : `-${currency(Math.round(Math.abs(propertyCashflow)))}`}/mo
          </div>
        </div>
        <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-center">
          <div className="text-[10px] font-medium uppercase tracking-wide text-amber-600">Net fund position</div>
          <div className={`text-sm font-semibold ${netFundPosition >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            {netFundPosition >= 0 ? `+${currency(Math.round(netFundPosition))}` : `-${currency(Math.round(Math.abs(netFundPosition)))}`}/mo
          </div>
        </div>
      </div>
    </div>
  );
}
